import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalProps, Button, Empty, Tag, Space, Tooltip, message, Checkbox, Select } from "antd";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useDroppable,
  useDraggable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useVirtualizer } from "@tanstack/react-virtual";
import { BiCheckDouble } from "react-icons/bi";
import * as TbIcons from "react-icons/tb";
import { IconType } from "react-icons";
import { CardDto } from "dtos/kanban.dto";
import { ApiService } from "api/api.service";
import { GROUP_COLOR_PALETTE } from "./MetaItems";
import { DEFAULT_COLOR, GroupColor } from "./BoardSectionList";
import TaskItem from "./TaskItem";

const UNGROUPED_KEY = "__ungrouped__";

export interface AssignCardsModalProps extends Omit<ModalProps, "onOk"> {
  boardId?: string;
  cards: CardDto[];
  refetch?: () => unknown;
  onAssign: (assignments: { cardId: string; columnId: string }[]) => Promise<void> | void;
}
const DraggableCard = ({
  card,
  isAssigned,
  isSelected,
  onSelect,
  showSelector,
}: {
  card: CardDto;
  isAssigned: boolean;
  isSelected: boolean;
  onSelect: (id: string, checked: boolean) => void;
  showSelector: boolean;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: card._id,
    disabled: isAssigned || isSelected,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      className={`relative transition-opacity ${isDragging ? "opacity-30" : ""} ${
        isAssigned ? "opacity-40 pointer-events-none" : ""
      }`}
    >
      <TaskItem
        itemData={card}
        dragListeners={listeners}
        select={{
          show: showSelector,
          checked: isSelected,
          onCheck(check) {
            onSelect(card._id, check);
          },
        }}
      />
    </div>
  );
};

const ColumnDropZone = ({
  columnId,
  columnName,
  columnIcon,
  columnDescription,
  assignedCount,
  borderColor,
  solidColor,
  softColor,
  disabled,
}: {
  columnId: string;
  columnName: string;
  columnIcon?: string;
  columnDescription?: string;
  assignedCount: number;
  borderColor: string;
  solidColor: string;
  softColor: string;
  disabled?: boolean;
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: columnId, disabled });
  const Icon = (columnIcon && (TbIcons as Record<string, IconType>)[columnIcon]) || null;

  return (
    <div
      ref={setNodeRef}
      className={`bg-white rounded-lg px-3 py-2 border flex items-center justify-between gap-2 transition-all ${
        isOver && !disabled ? "ring-2 ring-violet-500 scale-[1.01]" : ""
      } ${disabled ? "opacity-50" : ""}`}
      style={{ borderColor: isOver && !disabled ? "#8b5cf6" : borderColor }}
    >
      <Space>
        {Icon && (
          <div
            className="size-8 flex items-center justify-center rounded-md"
            style={{ backgroundColor: softColor, color: solidColor }}
          >
            <Icon size={16} />
          </div>
        )}
        <Space.Compact direction="vertical">
          <span className="text-xs font-medium truncate" title={columnName}>
            {columnName}
          </span>
          <span
            className={`text-[10px] font-medium truncate ${
              columnDescription?.length ? "text-gray-400" : "italic text-gray-400/80"
            }`}
            title={columnDescription || "No Description"}
          >
            {columnDescription || "No Description"}
          </span>
        </Space.Compact>
      </Space>
      {assignedCount > 0 ? (
        <Tag bordered={false} className="!m-0 !text-xs !px-1.5 !py-0 !bg-violet-100 !text-violet-700 flex-shrink-0">
          +{assignedCount}
        </Tag>
      ) : (
        <Tag bordered={false} className="!m-0 !text-xs !px-1.5 !py-0 !bg-gray-100 flex-shrink-0">
          0
        </Tag>
      )}
    </div>
  );
};

const KanbanEmptyImage = ({ size = 160 }: { size?: number }) => (
  <svg
    width={size}
    height={(size * 300) / 680}
    viewBox="0 0 680 300"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      display: "block",
    }}
  >
    <rect x="215" y="50" width="250" height="110" rx="8" fill="#f9fafb" stroke="#e5e7eb" strokeWidth="1.5" />
    <rect x="233" y="68" width="120" height="8" rx="4" fill="#e5e7eb" />
    <rect x="233" y="84" width="196" height="6" rx="3" fill="#f3f4f6" />
    <rect x="233" y="96" width="160" height="6" rx="3" fill="#f3f4f6" />
    <rect x="233" y="112" width="48" height="14" rx="4" fill="#f3f4f6" stroke="#e5e7eb" strokeWidth="1" />
    <circle cx="438" cy="130" r="10" fill="#f3f4f6" stroke="#e5e7eb" strokeWidth="1" />
    <circle cx="424" cy="130" r="10" fill="#f3f4f6" stroke="#e5e7eb" strokeWidth="1" />
    <rect x="233" y="126" width="60" height="6" rx="3" fill="#f3f4f6" />
    <rect x="215" y="172" width="250" height="110" rx="8" fill="#f3f4f6" stroke="#e5e7eb" strokeWidth="1.5" />
    <rect x="233" y="190" width="88" height="8" rx="4" fill="#e5e7eb" />
    <rect x="233" y="206" width="196" height="6" rx="3" fill="#e9eaeb" />
    <rect x="233" y="218" width="140" height="6" rx="3" fill="#e9eaeb" />
    <rect x="233" y="234" width="48" height="14" rx="4" fill="#ebebeb" stroke="#e0e0e0" strokeWidth="1" />
    <circle cx="438" cy="252" r="10" fill="#ebebeb" stroke="#e0e0e0" strokeWidth="1" />
    <circle cx="424" cy="252" r="10" fill="#ebebeb" stroke="#e0e0e0" strokeWidth="1" />
    <rect x="233" y="248" width="60" height="6" rx="3" fill="#e9eaeb" />
  </svg>
);

export const AssignCardsModal = (props: AssignCardsModalProps) => {
  const { boardId, cards, onAssign, refetch, ...modalProps } = props;
  const title = props.title || "Assign Cards";

  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(new Set());
  const [bulkColumnId, setBulkColumnId] = useState<string | undefined>();
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const parentRef = useRef<HTMLDivElement>(null);

  const boardQuery = ApiService.kanban.useGetBoardData(boardId);
  const columns = useMemo(() => boardQuery.data?.result?.columns ?? [], [boardQuery.data]);
  const groups = useMemo(() => boardQuery.data?.result?.groups ?? [], [boardQuery.data]);

  useEffect(() => {
    setSelectionEnabled(cards.length > 25);
  }, [cards]);

  const virtualizer = useVirtualizer({
    count: cards.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 180,
    measureElement: (el) => el.getBoundingClientRect().height,
    overscan: 5,
  });

  const groupColorMap = useMemo(() => {
    const map: Record<string, GroupColor> = {};
    for (const g of groups) {
      const colorKey = ((g.meta?.color as string) ?? "").replace("#", "").toLocaleLowerCase();
      const color = GROUP_COLOR_PALETTE.find((c) =>
        [c.solid, c.soft, c.border].some((hex) => hex.replace("#", "").toLocaleLowerCase() === colorKey),
      );
      if (color) map[g.name] = color;
    }
    map[UNGROUPED_KEY] = GROUP_COLOR_PALETTE[GROUP_COLOR_PALETTE.length - 1];
    return map;
  }, [groups]);

  const groupedColumns = useMemo(() => {
    const sorted = [...columns].sort((a, b) => a.position - b.position);
    const buckets = new Map<string, typeof sorted>();

    sorted.forEach((col) => {
      const key = col.group ?? UNGROUPED_KEY;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(col);
    });

    const orderedGroups = [...groups].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const result: { name: string; key: string; columns: typeof sorted }[] = [];

    const ungrouped = buckets.get(UNGROUPED_KEY);
    if (ungrouped?.length) {
      result.push({ name: "Ungrouped", key: UNGROUPED_KEY, columns: ungrouped });
      buckets.delete(UNGROUPED_KEY);
    }

    orderedGroups.forEach((g) => {
      const cols = buckets.get(g.name);
      if (cols?.length) result.push({ name: g.name, key: g.name, columns: cols });
      buckets.delete(g.name);
    });

    buckets.forEach((cols, name) => {
      if (cols.length) result.push({ name, key: name, columns: cols });
    });

    return result;
  }, [columns, groups]);

  const assignmentsByColumn = useMemo(() => {
    const map: Record<string, number> = {};
    Object.values(assignments).forEach((columnId) => {
      map[columnId] = (map[columnId] ?? 0) + 1;
    });
    return map;
  }, [assignments]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveCardId(active.id as string);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveCardId(null);
    if (!over) return;

    const columnId = over.id as string;
    const targetColumn = columns.find((col) => col.id === columnId);
    if (!targetColumn) return;
    if (targetColumn.disabled) {
      message.warning(`Cannot assign to disabled column: ${targetColumn.name}`);
      return;
    }

    setAssignments((prev) => ({ ...prev, [active.id as string]: columnId }));
  };

  const handleClearCard = (cardId: string) => {
    setAssignments((prev) => {
      const next = { ...prev };
      delete next[cardId];
      return next;
    });

    setSelectedCardIds((prev) => {
      const next = new Set(prev);
      next.delete(cardId);
      return next;
    });
  };

  const handleOk = async () => {
    const payload = Object.entries(assignments).map(([cardId, columnId]) => ({ cardId, columnId }));
    if (!payload.length) {
      message.warning("Drag at least one card onto a column to assign");
      return;
    }
    try {
      setSubmitting(true);
      await onAssign(payload);
      setAssignments({});
      refetch?.();
      modalProps.onCancel?.({} as any);
    } catch (err: any) {
      message.error(err?.message || "Failed to assign cards");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = (e: any) => {
    setAssignments({});
    setSelectedCardIds(new Set());
    setBulkColumnId(undefined);
    modalProps.onCancel?.(e);
  };

  const handleSelectCard = (cardId: string, checked: boolean) => {
    setSelectedCardIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(cardId) : next.delete(cardId);
      return next;
    });
  };

  const unassignedCardIds = useMemo(
    () => cards.filter((c) => !assignments[c._id]).map((c) => c._id),
    [cards, assignments],
  );

  const handleSelectAll = (checked: boolean) => {
    setSelectedCardIds(checked ? new Set(unassignedCardIds) : new Set());
  };

  const handleBulkAssign = () => {
    if (!bulkColumnId || selectedCardIds.size === 0) return;
    const targetColumn = columns.find((col) => col.id === bulkColumnId);
    if (targetColumn?.disabled) {
      message.warning(`Cannot assign to disabled column: ${targetColumn.name}`);
      return;
    }
    setAssignments((prev) => {
      const next = { ...prev };
      selectedCardIds.forEach((id) => {
        next[id] = bulkColumnId;
      });
      return next;
    });
    setSelectedCardIds(new Set());
    setBulkColumnId(undefined);
  };

  const activeCard = activeCardId ? cards.find((c) => c._id === activeCardId) : null;
  const assignedCount = Object.keys(assignments).length;
  const unassignedCards = cards.filter((c) => !assignments[c._id]);

  return (
    <Modal
      {...modalProps}
      title={title}
      onCancel={handleCancel}
      width={1080}
      className={`!top-8 ${modalProps.className ?? ""}`}
      footer={
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {assignedCount} of {cards.length} card{cards.length === 1 ? "" : "s"} staged
          </span>
          <Space>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" loading={submitting} disabled={assignedCount === 0} onClick={handleOk}>
              Assign {assignedCount > 0 ? `(${assignedCount})` : ""}
            </Button>
          </Space>
        </div>
      }
    >
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="grid grid-cols-2 gap-4 h-[70vh] min-h-0">
          <div className="flex flex-col rounded-xl border border-gray-200 bg-gray-50 overflow-hidden min-h-0">
            <div className="px-4 py-2.5 border-b border-gray-200 bg-white flex items-center justify-between flex-shrink-0">
              <Space>
                {selectionEnabled ? (
                  <Tooltip key="single-select" title="Drag & Drop">
                    <Button
                      size="small"
                      variant="outlined"
                      color="green"
                      icon={<TbIcons.TbDragDrop />}
                      onClick={() => {
                        setSelectionEnabled(false);
                        handleSelectAll(false);
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip key="multi-select" title="Multi Select">
                    <Button
                      size="small"
                      variant="outlined"
                      color="blue"
                      icon={<TbIcons.TbListCheck />}
                      onClick={() => setSelectionEnabled(true)}
                    />
                  </Tooltip>
                )}
                {selectionEnabled && selectedCardIds.size < cards.length && (
                  <Tooltip key="select-all" title="Select All">
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      icon={<BiCheckDouble />}
                      onClick={() => handleSelectAll(true)}
                    />
                  </Tooltip>
                )}
                {selectionEnabled && selectedCardIds.size > 0 && (
                  <Tooltip key="unselect-all" title="Unselect All">
                    <Button
                      size="small"
                      variant="outlined"
                      color="default"
                      icon={<TbIcons.TbMinus />}
                      onClick={() => handleSelectAll(false)}
                    />
                  </Tooltip>
                )}
              </Space>
              <Space>
                {selectedCardIds.size > 0 && (
                  <Tag bordered={false} className="!m-0 !bg-violet-100 !text-violet-700 !font-medium">
                    {selectedCardIds.size} selected
                  </Tag>
                )}
                <Tag bordered={false} className="!m-0 !bg-gray-100 !font-medium">
                  {unassignedCards.length} pending
                </Tag>
              </Space>
            </div>
            <div ref={parentRef} className="flex-1 overflow-y-auto min-h-0 p-3">
              {cards.length === 0 ? (
                <div className="flex items-center justify-center size-full">
                  <Empty
                    image={<KanbanEmptyImage size={2000} />}
                    description="No cards to assign"
                    styles={{
                      image: {
                        height: "auto",
                      },
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    height: virtualizer.getTotalSize(),
                    width: "100%",
                    position: "relative",
                  }}
                >
                  {virtualizer.getVirtualItems().map((virtualItem) => {
                    const card = cards[virtualItem.index];
                    if (!card) return null;

                    const stagedColumnId = assignments[card._id];
                    const stagedColumn = stagedColumnId ? columns.find((c) => c.id === stagedColumnId) : null;

                    return (
                      <div
                        key={card._id}
                        data-index={virtualItem.index}
                        ref={virtualizer.measureElement}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          transform: `translateY(${virtualItem.start}px)`,
                          paddingBottom: 12,
                        }}
                      >
                        <div className="relative h-full">
                          <DraggableCard
                            card={card}
                            isAssigned={!!stagedColumnId}
                            isSelected={selectedCardIds.has(card._id)}
                            onSelect={handleSelectCard}
                            showSelector={selectionEnabled}
                          />
                          {stagedColumn && (
                            <div className="absolute inset-0 rounded-xl bg-white/80 backdrop-blur-sm flex items-center justify-between px-4 border border-solid border-violet-200">
                              <Space>
                                <TbIcons.TbCircleCheckFilled className="text-violet-600" size={18} />
                                <Space.Compact direction="vertical">
                                  <span className="text-[10px] text-gray-500 leading-3">Staged for</span>
                                  <span className="text-sm font-semibold text-gray-800">{stagedColumn.name}</span>
                                </Space.Compact>
                              </Space>
                              <Tooltip title="Undo">
                                <Button
                                  size="small"
                                  type="text"
                                  icon={<TbIcons.TbX size={14} />}
                                  onClick={() => handleClearCard(card._id)}
                                />
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {selectedCardIds.size > 0 && (
              <div className="px-3 py-2 border-t border-gray-200 bg-gray-50 flex items-center gap-2 flex-shrink-0">
                <Select
                  placeholder="Assign selected to column…"
                  className="flex-1"
                  prefix={<TbIcons.TbLayoutKanban size={16} className="text-violet-500 flex-shrink-0" />}
                  value={bulkColumnId}
                  onChange={setBulkColumnId}
                  options={columns
                    .filter((c) => !c.disabled)
                    .sort((a, b) => a.position - b.position)
                    .map((c) => ({ label: c.name, value: c.id }))}
                />
                <Space>
                  <Button
                    type="primary"
                    disabled={!bulkColumnId}
                    onClick={handleBulkAssign}
                    icon={<TbIcons.TbCheck size={14} />}
                  />
                  <Button
                    type="default"
                    icon={<TbIcons.TbX size={14} />}
                    onClick={() => {
                      setSelectedCardIds(new Set());
                      setBulkColumnId(undefined);
                    }}
                  />
                </Space>
              </div>
            )}
          </div>
          <div className="flex flex-col rounded-xl border border-gray-200 bg-gray-50 overflow-hidden min-h-0">
            <div className="px-4 py-2.5 border-b border-gray-200 bg-white flex items-center justify-between flex-shrink-0">
              <span className="font-semibold text-sm">Drop into a column</span>
              {assignedCount > 0 && (
                <Button size="small" type="link" onClick={() => setAssignments({})}>
                  Clear all
                </Button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-3 min-h-0">
              {groupedColumns.length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No columns" />
                </div>
              ) : (
                groupedColumns.map((group) => {
                  const color = groupColorMap[group.key] ?? DEFAULT_COLOR;
                  const isCollapsed = collapsedGroups.includes(group.key);
                  const groupAssigned = group.columns.reduce((sum, col) => sum + (assignmentsByColumn[col.id] ?? 0), 0);

                  return (
                    <div
                      key={group.key}
                      className="rounded-xl border overflow-hidden flex-shrink-0"
                      style={{ backgroundColor: color.soft, borderColor: color.border }}
                    >
                      <button
                        type="button"
                        className="w-full flex items-center justify-between px-3 py-2 text-white outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-inset border-0 cursor-pointer"
                        style={{ backgroundColor: color.solid }}
                        onClick={() =>
                          setCollapsedGroups((prev) =>
                            prev.includes(group.key) ? prev.filter((k) => k !== group.key) : [...prev, group.key],
                          )
                        }
                      >
                        <Space>
                          {isCollapsed ? <TbIcons.TbChevronRight size={16} /> : <TbIcons.TbChevronDown size={16} />}
                          <span className="font-semibold text-sm">{group.name}</span>
                        </Space>
                        <Space>
                          <span
                            className="text-[10px] px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: "rgba(255,255,255,0.25)" }}
                          >
                            {group.columns.length} col{group.columns.length === 1 ? "" : "s"}
                          </span>
                          {groupAssigned > 0 && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white text-violet-700 font-semibold">
                              +{groupAssigned}
                            </span>
                          )}
                        </Space>
                      </button>
                      {!isCollapsed && (
                        <div className="flex flex-col gap-2 p-2">
                          {group.columns.map((column) => (
                            <ColumnDropZone
                              key={column.id}
                              columnId={column.id}
                              columnName={column.name}
                              columnIcon={column.meta?.icon}
                              columnDescription={column.meta?.description}
                              assignedCount={assignmentsByColumn[column.id] ?? 0}
                              borderColor={color.border}
                              solidColor={color.solid}
                              softColor={color.soft}
                              disabled={column.disabled}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <DragOverlay>{activeCard ? <TaskItem itemData={activeCard} /> : null}</DragOverlay>
      </DndContext>
    </Modal>
  );
};
